/**
 * Work 템플릿 작성/편집 화면의 블록 목록: 추가·제거·정렬·제출 시 인덱스 재번호.
 */
import Sortable from "sortablejs";

type SwiperPaletteCtor = new (
    el: Element,
    options: Record<string, unknown>,
) => {
    update: () => void;
};

export interface WorkTemplateBlocksBuilderOptions {
    /** 블록 순서·개편 후 호출 (미리보기 디바운스 등) */
    onStructureChange?: () => void;
}

function reindexBlockInputs(list: HTMLElement): void {
    const items = list.querySelectorAll<HTMLElement>("[data-work-block-item]");
    items.forEach((item, newIdx) => {
        item.querySelectorAll<HTMLElement>("[name^='blocks[']").forEach((el) => {
            const name = el.getAttribute("name");
            if (!name?.startsWith("blocks[")) return;
            el.setAttribute("name", name.replace(/^blocks\[[^\]]+]/, `blocks[${newIdx}]`));
        });
    });
}

export class WorkTemplateBlocksBuilder {
    readonly #root: HTMLElement;
    readonly #opts: WorkTemplateBlocksBuilderOptions;
    #list: HTMLElement | null = null;
    #palette: HTMLElement | null = null;

    constructor(root: HTMLElement, opts: WorkTemplateBlocksBuilderOptions = {}) {
        this.#root = root;
        this.#opts = opts;
    }

    getList(): HTMLElement | null {
        return this.#list;
    }

    init(): void {
        this.#list = this.#root.querySelector<HTMLElement>("[data-work-blocks-list]");
        if (!this.#list) return;
        this.#palette = this.#root.querySelector<HTMLElement>("[data-work-blocks-palette]");

        let counter = this.#root.querySelectorAll("[data-work-block-item]").length + 1;
        const allocateIdx = (): number => {
            const seed = Date.now() + counter++;
            return seed % 1_000_000_000;
        };

        const listSortable = Sortable.create(this.#list, {
            animation: 150,
            handle: "[data-work-blocks-drag]",
            ghostClass: "admin-sortable__ghost",
            dragClass: "admin-sortable__drag",
            group: this.#palette ? { name: "workBlocks", pull: true, put: true } : undefined,
            onEnd: () => this.#opts.onStructureChange?.(),
            onAdd: (evt) => {
                // Dragged from palette → replace placeholder with a real block node.
                const fromPalette = !!evt.from?.closest?.("[data-work-blocks-palette]");
                if (!fromPalette) return;
                const slug =
                    (evt.item as HTMLElement | null)?.getAttribute?.("data-section-slug") ??
                    (evt.item as HTMLElement | null)?.getAttribute?.("data-work-blocks-add") ??
                    "";
                if (!slug || !this.#list) return;

                const tpl = this.#root.querySelector<HTMLTemplateElement>(
                    `template[data-work-block-template="${slug}"]`,
                );
                if (!tpl) {
                    // If template is missing, just remove the dropped item.
                    evt.item.remove();
                    return;
                }

                const idx = String(allocateIdx());
                const html = tpl.innerHTML.replace(/__IDX__/g, idx);
                const wrap = document.createElement("div");
                wrap.innerHTML = html.trim();
                const node = wrap.firstElementChild as HTMLElement | null;
                if (!node) {
                    evt.item.remove();
                    return;
                }

                this.#bindRemove(node);

                const children = Array.from(this.#list.children);
                const insertAt = typeof evt.newIndex === "number" ? evt.newIndex : children.length;
                // Remove placeholder (palette clone)
                evt.item.remove();
                // Insert at drop position (best-effort)
                const ref = this.#list.children.item(insertAt) as Element | null;
                if (ref) {
                    this.#list.insertBefore(node, ref);
                } else {
                    this.#list.appendChild(node);
                }

                this.#opts.onStructureChange?.();
            },
        });

        if (this.#palette) {
            // Palette: draggable source (clone only)
            Sortable.create(this.#palette, {
                animation: 150,
                group: { name: "workBlocks", pull: "clone", put: false },
                sort: false,
                draggable: "[data-work-blocks-palette-item]",
                filter: "a,button,input,textarea,select,option",
                preventOnFilter: false,
                onClone: (evt) => {
                    // Ensure the clone has slug metadata for onAdd.
                    const el = evt.clone as HTMLElement;
                    const slug = el.getAttribute("data-section-slug") ?? "";
                    if (slug) el.setAttribute("data-section-slug", slug);
                },
            });
        }

        this.#root.querySelectorAll<HTMLButtonElement>("[data-work-blocks-add]").forEach((btn) => {
            btn.addEventListener("click", () => {
                const slug = btn.getAttribute("data-work-blocks-add") ?? "";
                const tpl = this.#root.querySelector<HTMLTemplateElement>(
                    `template[data-work-block-template="${slug}"]`,
                );
                if (!tpl || !this.#list) return;
                const idx = String(allocateIdx());
                const html = tpl.innerHTML.replace(/__IDX__/g, idx);
                const wrap = document.createElement("div");
                wrap.innerHTML = html.trim();
                const node = wrap.firstElementChild as HTMLElement | null;
                if (!node) return;
                this.#bindRemove(node);
                this.#list.appendChild(node);
                this.#opts.onStructureChange?.();
            });
        });

        this.#list.querySelectorAll<HTMLElement>("[data-work-block-item]").forEach((item) => {
            this.#bindRemove(item);
        });

        this.#initBlocksPaletteSwiper();
    }

    /** 템플릿 캔버스 툴바 팔레트: 좌우 화살표로 슬라이드 (전역 Swiper 번들) */
    #initBlocksPaletteSwiper(): void {
        const swiperEl = this.#root.querySelector<HTMLElement>(".admin-work-blocks-palette-swiper");
        if (!swiperEl) return;
        const Swiper = (window as unknown as { Swiper?: SwiperPaletteCtor }).Swiper;
        if (!Swiper) return;

        const host = swiperEl.closest(".admin-work-blocks-palette__swiper-host");
        const prev = host?.querySelector<HTMLElement>("[data-admin-blocks-palette-prev]") ?? null;
        const next = host?.querySelector<HTMLElement>("[data-admin-blocks-palette-next]") ?? null;

        const swiper = new Swiper(swiperEl, {
            slidesPerView: "auto",
            spaceBetween: 8,
            watchOverflow: true,
            freeMode: true,
            navigation:
                prev && next
                    ? {
                          prevEl: prev,
                          nextEl: next,
                      }
                    : undefined,
        });
        requestAnimationFrame(() => swiper.update());
    }

    #bindRemove(item: HTMLElement): void {
        const btn = item.querySelector<HTMLButtonElement>("[data-work-blocks-remove]");
        if (!btn) return;
        btn.addEventListener("click", () => {
            item.remove();
            this.#opts.onStructureChange?.();
        });
    }

    /** AJAX 폼(Form.ts)보다 먼저 실행되어 FormData 에 반영됨 */
    bindSubmitReindex(form: HTMLFormElement): void {
        const list = this.#list;
        if (!list) return;
        form.addEventListener(
            "submit",
            () => {
                reindexBlockInputs(list);
            },
            true,
        );
    }
}
